import { useContext, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { getAuthUser } from '@/lib/auth-bridge';
import { AuthContext } from '@/providers/auth-context';
import type { FeatureFlag } from '@/config/feature-flags';
import { FLAG_TO_SECTION, DEFAULT_ENABLED_SECTIONS } from '@/config/feature-flags';
import type { DashboardSection } from '@/components/dashboard/DashboardSidebar';

export function useFeatureFlags(category: string = 'dashboard') {
  // `feature_flags` has an authenticated-only SELECT policy, so WHO is asking
  // determines the answer. Key the cache by auth identity and hold the query
  // until auth has resolved: under the Clerk engine (SSO.4/REK-1990), a hard
  // navigation cold-inits clerk-js, and a flags query racing the token closure
  // used to resolve as `anon`, cache `[]` under the shared key for the 60s
  // staleTime, and bounce every gated dashboard path to Overview. With the
  // identity in the key, the post-sign-in render reads a FRESH entry fetched
  // with the real token — an anon-empty result can never be served to an
  // authenticated render.
  //
  // Read the context non-throwing (plain useContext, not useAuthContext):
  // outside an AuthProvider — isolated component tests, Storybook stories —
  // there is no auth lifecycle to wait on, and the pre-REK-1990 behavior
  // (fire immediately under the anon key) is exactly right.
  const auth = useContext(AuthContext);
  const authLoading = auth?.isLoading ?? false;
  const authUserId = auth?.user?.id ?? 'anon';

  const { data: flags, isLoading, error } = useQuery({
    queryKey: ['feature-flags', category, authUserId],
    enabled: !authLoading,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('feature_flags')
        .select('*')
        .eq('category', category)
        .order('label');
      
      if (error) throw error;
      return data as FeatureFlag[];
    },
    staleTime: 60000, // Cache for 1 minute
  });

  const enabledSections = useMemo<DashboardSection[]>(() => {
    if (!flags) return DEFAULT_ENABLED_SECTIONS;
    
    return flags
      .filter(f => f.enabled)
      .map(f => FLAG_TO_SECTION[f.flag_key])
      .filter((s): s is DashboardSection => s !== undefined);
  }, [flags]);

  const isSectionEnabled = (section: DashboardSection): boolean => {
    return enabledSections.includes(section);
  };

  const isFlagEnabled = (flagKey: string): boolean => {
    if (!flags) return false;
    return flags.some(f => f.flag_key === flagKey && f.enabled);
  };

  return { 
    flags, 
    enabledSections, 
    isSectionEnabled,
    isFlagEnabled,
    isLoading, 
    error 
  };
}

/**
 * True when admin has enabled the Protect dashboard module (`dashboard.breaches`).
 * Use to hide Protect cross-sells, overview widgets, and navigation to `/dashboard/breaches`.
 */
export function useProtectDashboardEnabled(): boolean {
  const { isSectionEnabled } = useFeatureFlags('dashboard');
  return isSectionEnabled('breaches');
}

/**
 * True when admin has enabled user tier badges (`dashboard.tiers`).
 * Fail-closed: returns false while flags are loading or on error.
 */
export function useTierBadgesEnabled(): boolean {
  const { isFlagEnabled, isLoading, error } = useFeatureFlags('dashboard');
  if (isLoading || error) return false;
  return isFlagEnabled('dashboard.tiers');
}

/**
 * True when admin has enabled SMS-first verification and Ruby SMS channel UI.
 * Fail-closed: returns false while flags are loading or on error.
 */
export function useIsSmsFirstVerificationEnabled(): boolean {
  const { isFlagEnabled, isLoading, error } = useFeatureFlags('features');
  if (isLoading || error) return false;
  return isFlagEnabled('sms_first_verification');
}

export function useUpdateFeatureFlag() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, enabled }: { id: string; enabled: boolean }) => {
      const user = await getAuthUser();
      
      const { data, error } = await supabase
        .from('feature_flags')
        .update({ 
          enabled, 
          updated_by: user?.id,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feature-flags'] });
    },
  });
}
